import type { HrProcessAuditEntry } from "../../../lib/api";
import { formatSp } from "../../../lib/datetime";

/** Mapa field → label (espelha os campos auditados no backend). */
const FIELD_LABELS: Record<string, string> = {
  recruiter: "Recrutador",
  responsible_manager: "Gestor responsável",
  job_title: "Cargo",
  job_activities: "Atividades do cargo",
  salary: "Salário",
  cost_center: "Centro de custo",
  workplace: "Local de trabalho",
  work_schedule: "Escala",
  work_start_time: "Horário de início",
  work_end_time: "Horário de fim",
  admission_date: "Data de admissão",
  admission_exam_completed: "Exame admissional",
};

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

/** Formata um valor p/ exibição. Salário vira R$X; vazio vira "—"; data de admissão em DD/MM/AAAA. */
function formatValue(field: string, value: string | null): string {
  if (value === null || value === undefined || value === "") return "—";
  if (field === "salary" && value === "Restrito") return "Restrito";
  if (field === "salary") {
    const n = Number(String(value).replace(",", "."));
    return Number.isFinite(n)
      ? `R$ ${n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      : `R$ ${value}`;
  }
  if (field === "admission_date") {
    const [y, m, d] = String(value).split("-");
    return y && m && d ? `${d}/${m}/${y}` : String(value);
  }
  return String(value);
}

function formatDateTime(iso: string): string {
  const formatted = formatSp(iso, "dd/MM/yyyy HH:mm");
  return formatted || iso;
}

/**
 * Partes estruturadas de uma alteração — para a timeline compor com hierarquia
 * (ator / verbo / campo / valores). Não altera a semântica do formatAuditChange.
 */
export type AuditChangeType = "added" | "removed" | "changed" | "cleared";

export interface AuditParts {
  actor: string;
  field: string;
  fieldKey: string;
  changeType: AuditChangeType;
  oldValue: string;
  newValue: string;
  rawOld: string | null;
  rawNew: string | null;
}

export function describeAuditParts(entry: HrProcessAuditEntry): AuditParts {
  const hadValue = entry.old_value !== null && entry.old_value !== "";
  const hasValue = entry.new_value !== null && entry.new_value !== "";
  let changeType: AuditChangeType;
  if (!hadValue && hasValue) changeType = "added";
  else if (hadValue && !hasValue) changeType = "removed";
  else if (!hadValue && !hasValue) changeType = "cleared";
  else changeType = "changed";
  return {
    actor: entry.actor_name || entry.actor_email,
    field: fieldLabel(entry.field),
    fieldKey: entry.field,
    changeType,
    oldValue: formatValue(entry.field, entry.old_value),
    newValue: formatValue(entry.field, entry.new_value),
    rawOld: entry.old_value,
    rawNew: entry.new_value,
  };
}

const VERB: Record<AuditChangeType, string> = {
  added: "definiu",
  changed: "alterou",
  removed: "removeu",
  cleared: "removeu",
};

/**
 * Mensagem da alteração (sem candidato/data — o componente compõe):
 *  - valor adicionado (vazio → valor):  "X definiu o <campo> como <novo>"
 *  - valor removido   (valor → vazio):  "X removeu o <campo> (era <velho>)"
 *  - valor trocado    (valor → valor):  "X alterou o <campo> de <velho> para <novo>"
 */
export function formatAuditChange(entry: HrProcessAuditEntry): string {
  const p = describeAuditParts(entry);
  switch (p.changeType) {
    case "added":   return `${p.actor} ${VERB.added} o ${p.field} como ${p.newValue}`;
    case "removed": return `${p.actor} ${VERB.removed} o ${p.field} (era ${p.oldValue})`;
    case "cleared": return `${p.actor} ${VERB.cleared} o ${p.field}`;
    case "changed": return `${p.actor} ${VERB.changed} o ${p.field} de ${p.oldValue} para ${p.newValue}`;
  }
}

export { VERB as AUDIT_VERB };

export { formatDateTime };

export function candidateDisplayName(entry: HrProcessAuditEntry): string {
  return entry.candidate_name || entry.candidate_email;
}